// ScheduledPatchesCard.js
import React, { useState, useEffect } from "react";
import {
  Card, CardHeader, CardBody, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, FormGroup, Label, Input, Table, Spinner, Alert, Badge
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import patchService from "../../services/PatchService";

const ScheduledPatchesCard = ({ agents = [], scheduledJobs = [], refreshScheduledJobs }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [scheduleTime, setScheduleTime] = useState("");
  const [recurrence, setRecurrence] = useState("once");
  const [dayOfWeek, setDayOfWeek] = useState("1");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [loading, setLoading] = useState(false);
  const [jobList, setJobList] = useState(scheduledJobs);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (scheduledJobs.length === 0) loadScheduledJobs();
    else setJobList(scheduledJobs);
  }, [scheduledJobs]);

  const loadScheduledJobs = async () => {
    try {
      setLoading(true);
      const res = await patchService.getScheduledJobs();
      setJobList(res.scheduled_jobs || []);
      setError(null);
    } catch (err) {
      setError("Failed to load scheduled patch jobs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
    setSelectedAgents([]);
    setScheduleTime("");
    setRecurrence("once");
    setDayOfWeek("1");
    setDayOfMonth("1");
    setError(null);
  };

  const handleAgentSelection = (agentId) => {
    setSelectedAgents(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const handleSelectAllAgents = () => {
    const onlineAgentIds = onlineAgents.map(a => a.agent_id);
    if (selectedAgents.length === onlineAgentIds.length) {
      setSelectedAgents([]);
    } else {
      setSelectedAgents(onlineAgentIds);
    }
  };

  const getMinScheduleTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now.toISOString().slice(0, 16);
  };

  const handleSchedule = async () => {
    if (selectedAgents.length === 0) {
      setError("Please select at least one device");
      return;
    }

    if (!scheduleTime) {
      setError("Please select a scheduled time");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const scheduleDateTime = new Date(scheduleTime).toISOString();
      const recurrenceOptions = {};

      if (recurrence === 'weekly') {
        recurrenceOptions.day_of_week = parseInt(dayOfWeek);
      }
      if (recurrence === 'monthly') {
        recurrenceOptions.day_of_month = parseInt(dayOfMonth);
      }

      const result = await patchService.installPatches(
        selectedAgents,
        [],
        true,
        scheduleDateTime,
        recurrence,
        recurrenceOptions
      );

      console.log("Schedule result:", result);

      setShowModal(false);
      if (refreshScheduledJobs) await refreshScheduledJobs();

      alert(`Patch installation scheduled successfully for ${selectedAgents.length} agent(s)!`);
    } catch (err) {
      console.error("Error scheduling patch:", err);
      setError(`Failed to schedule patch installation: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled patch installation?')) {
      return;
    }

    try {
      setLoading(true);
      await patchService.cancelScheduledJob(jobId);
      if (refreshScheduledJobs) await refreshScheduledJobs();
    } catch (err) {
      console.error("Failed to cancel job:", err);
      alert("Failed to cancel scheduled job");
    } finally {
      setLoading(false);
    }
  };

  const onlineAgents = agents.filter((a) => a.status === "online");
  const getAgentById = (agentId) => agents.find(a => a.agent_id === agentId);

  return (
    <Card className="shadow-sm border-0" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
      <CardHeader style={{ backgroundColor: 'var(--bs-warning-bg)', borderBottom: '1px solid var(--bs-border-color)' }} className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0 fw-bold" style={{ color: 'var(--bs-body-color)' }}>
          <FeatherIcon icon="clock" size={18} className="me-2 text-primary" />
          Scheduled Patch Installations
        </h5>
        <Button color="primary" size="sm" onClick={toggleModal} className="btn btn-outline-primary">
          <FeatherIcon icon="plus" size={14} className="me-1" />
          Schedule New Patch
        </Button>
      </CardHeader>

      <CardBody>
        {loading && jobList.length === 0 ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="text-muted mt-2 mb-0">Loading scheduled patches...</p>
          </div>
        ) : jobList.length === 0 ? (
          <div className="text-center py-5">
            <FeatherIcon icon="clock" size={48} className="text-muted mb-3" />
            <p className="text-muted mb-3">No scheduled patches configured.</p>
            <Button color="outline-primary" onClick={toggleModal}>
              Create Your First Scheduled Patch
            </Button>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead style={{ backgroundColor: 'var(--bs-tertiary-bg)', color: 'var(--bs-body-color)' }}>
                <tr>
                  <th className="fw-semibold border-0">Job ID</th>
                  <th className="fw-semibold border-0">Device(s)</th>
                  <th className="fw-semibold border-0">Scheduled Time</th>
                  <th className="fw-semibold border-0">Recurrence</th>
                  <th className="fw-semibold border-0">Status</th>
                  <th className="fw-semibold border-0">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobList.map((job) => (
                  <tr key={job.job_id} >
                    <td>
                      <code className="small" style={{ backgroundColor: 'var(--bs-primary-bg)', color: 'var(--bs-code-color)' }}>
                        {job.job_id.substring(0, 8)}...
                      </code>
                    </td>
                    <td>
                      {job.agent_hostnames && job.agent_hostnames.length > 0 ? (
                        <>
                          {job.agent_hostnames.slice(0, 2).join(", ")}
                          {job.agent_hostnames.length > 2 && (
                            <Badge color="primary" className="ms-1">
                              +{job.agent_hostnames.length - 2} more
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td>
                      <FeatherIcon icon="calendar" size={14} className="me-1 text-primary" />
                      {new Date(job.scheduled_time).toLocaleString()}
                    </td>
                    <td>
                      <Badge color="" className="rounded-pill border"
                        style={{
                          backgroundColor: "rgba(255, 91, 0, 0.08)",
                          border: "1px solid #FF5B00",
                          color: "#FF5B00",
                          fontWeight: 500,
                          fontSize: "11px", padding: "4px 10px"
                        }} >
                        {job.recurrence || "once"} </Badge>
                    </td>
                    <td>
                      <Badge
                        color=""
                        className={`rounded-pill border fw-medium ${job.status === "completed"
                            ? "text-success border-success"
                            : job.status === "pending"
                              ? "text-warning border-warning"
                              : job.status === "running"
                                ? "text-primary border-primary"
                                : "text-danger border-danger"
                          }`}
                        style={{
                          backgroundColor:
                            job.status === "completed"
                              ? "rgba(34, 197, 94, 0.1)"
                              : job.status === "pending"
                                ? "rgba(255, 193, 7, 0.1)"
                                : job.status === "running"
                                  ? "rgba(59, 130, 246, 0.1)"
                                  : "rgba(239, 68, 68, 0.1)",
                          fontSize: "11px",
                          padding: "4px 10px"
                        }}
                      >
                        {job.status || "Pending"}
                      </Badge>
                    </td>

                    <td>
                      {job.status === 'pending' && (
                        <Button
                          color="outline-danger"
                          size="sm"
                          onClick={() => handleCancelJob(job.job_id)}
                          disabled={loading}
                        >
                          <FeatherIcon icon="x" size={14} className="me-1" />
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      {/* Schedule Modal */}
      <Modal isOpen={showModal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal} style={{ backgroundColor: 'var(--bs-primary-bg)', color: 'var(--bs-body-color)', borderBottom: '1px solid var(--bs-border-color)' }}>
          <FeatherIcon icon="clock" size={18} className="me-2 text-primary" />
          Schedule Automatic Patch Installation
        </ModalHeader>
        <ModalBody style={{ backgroundColor: 'var(--bs-body-bg)', color: 'var(--bs-body-color)' }}>
          {error && (
            <Alert color="danger" className="mb-3">
              <FeatherIcon icon="alert-circle" size={16} className="me-2" />
              {error}
            </Alert>
          )}

          {/* Agent Selection */}
          <FormGroup>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Label className="mb-0">Select Devices *</Label>
              <Button
                color="link"
                size="sm"
                onClick={handleSelectAllAgents}
                className="text-decoration-none"
              >
                {selectedAgents.length === onlineAgents.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {onlineAgents.length === 0 ? (
              <Alert color="primary">
                <FeatherIcon icon="alert-triangle" size={16} className="me-2" />
                No online devices available
              </Alert>
            ) : (
              <div className="border rounded p-3" style={{
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: 'var(--bs-primary-bg)',
                borderColor: 'var(--bs-border-color)'
              }}>
                {onlineAgents.map((agent) => {
                  const pendingCount = agent.patch_data?.data?.data?.updates?.length || 0;
                  return (
                    <div key={agent.agent_id} className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`agent-${agent.agent_id}`}
                        checked={selectedAgents.includes(agent.agent_id)}
                        onChange={() => handleAgentSelection(agent.agent_id)}
                      />
                      <label
                        className="form-check-label d-flex justify-content-between w-100"
                        htmlFor={`agent-${agent.agent_id}`}
                        style={{ color: 'var(--bs-body-color)' }}
                      >
                        <span>
                          <FeatherIcon icon="monitor" size={14} className="me-2 text-primary" />
                          {agent.hostname} ({agent.ip_address})
                        </span>
                        <Badge color={pendingCount === 0 ? 'success' : 'primary'} className="bg-transparent" style={{ backgroundColor: "#dba56659", border: "1px solid #db6c12ff" }}>
                          {pendingCount} pending
                        </Badge>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
            <small className="text-muted">
              {selectedAgents.length} device(s) selected
            </small>
          </FormGroup>

          {/* Schedule Time */}
          <FormGroup>
            <Label>Scheduled Time *</Label>
            <Input
              type="datetime-local"
              value={scheduleTime}
              min={getMinScheduleTime()}
              onChange={(e) => setScheduleTime(e.target.value)}
              style={{
                backgroundColor: 'var(--bs-body-bg)',
                borderColor: 'var(--bs-border-color)',
                color: 'var(--bs-body-color)'
              }}
            />
            <small className="text-muted">Minimum 5 minutes from now</small>
          </FormGroup>

          {/* Recurrence Pattern */}
          <FormGroup>
            <Label>Recurrence Pattern</Label>
            <Input
              type="select"
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              style={{
                backgroundColor: 'var(--bs-body-bg)',
                borderColor: 'var(--bs-border-color)',
                color: 'var(--bs-body-color)'
              }}
            >
              <option value="once">One-time installation</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Input>
          </FormGroup>

          {/* Weekly Day Selection */}
          {recurrence === 'weekly' && (
            <FormGroup>
              <Label>Day of Week</Label>
              <Input
                type="select"
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(e.target.value)}
                style={{
                  backgroundColor: 'var(--bs-body-bg)',
                  borderColor: 'var(--bs-border-color)',
                  color: 'var(--bs-body-color)'
                }}
              >
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
                <option value="0">Sunday</option>
              </Input>
            </FormGroup>
          )}

          {/* Monthly Day Selection */}
          {recurrence === 'monthly' && (
            <FormGroup>
              <Label>Day of Month</Label>
              <Input
                type="select"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
                style={{
                  backgroundColor: 'var(--bs-body-bg)',
                  borderColor: 'var(--bs-border-color)',
                  color: 'var(--bs-body-color)'
                }}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </Input>
              <small className="text-muted">Limited to day 1-28 for consistency</small>
            </FormGroup>
          )}

          {/* Summary */}
          {selectedAgents.length > 0 && scheduleTime && (
            <Alert color="warning" className="mb-0">
              <h6 className="alert-heading">
                <FeatherIcon icon="warning" size={16} className="me-2" />
                Summary
              </h6>
              <div><strong>Devices:</strong> {selectedAgents.length} selected</div>
              <div><strong>Action:</strong> Install all pending patches</div>
              <div>
                <strong>Schedule:</strong> {new Date(scheduleTime).toLocaleString()}
                {recurrence !== 'once' && (
                  <Badge color="primary" className="ms-2">{recurrence}</Badge>
                )}
              </div>
              {selectedAgents.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    Selected: {selectedAgents.map(id => getAgentById(id)?.hostname).join(', ')}
                  </small>
                </div>
              )}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter style={{ backgroundColor: 'var(--bs-body-bg)', borderTop: '1px solid var(--bs-border-color)' }}>
          <Button color="primary" onClick={toggleModal} disabled={loading} className="btn btn-outline-primary">
            Cancel
          </Button>
          <Button
            color="primary" className="btn btn-outline-primary"
            onClick={handleSchedule}
            disabled={loading || selectedAgents.length === 0 || !scheduleTime}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" /> Scheduling...
              </>
            ) : (
              <>
                <FeatherIcon icon="check-circle" size={14} className="me-1" />
                Schedule Patch
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default ScheduledPatchesCard;